import { useEffect, useState } from "react";
import { Search, Loader2, Building2, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";

export const emptyCustomer = { type: "new", number: "", business: null };

export const customerError = (c) =>
  c.type === "existing" && !c.business ? "Please enter a valid 6-digit Customer Number." : "";

export const customerPayload = (c) =>
  c.type === "existing" ? { customer_type: "existing", business_number: c.number } : { customer_type: "new" };

function TypeButton({ active, onClick, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
      className={`h-11 rounded-xl text-sm font-bold border transition-colors ${active ? "bg-[#FFC400] border-[#FFC400] text-[#1A1500]" : "bg-white border-slate-200 text-slate-600 hover:border-[#FFC400]"}`}>
      {label}
    </button>
  );
}

export const CustomerTypePicker = ({ value, onChange, prefix }) => {
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const { type, number } = value;

  useEffect(() => {
    if (type !== "existing" || number.length !== 6) { setLookupError(""); return; }
    let alive = true;
    setLoading(true);
    api.get(`/public/business-lookup/${number}`)
      .then((b) => { if (alive) { onChange({ type, number, business: b }); setLookupError(""); } })
      .catch((e) => { if (alive) setLookupError(apiError(e)); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, number]);

  const setNumber = (v) => onChange({ type, number: v.replace(/\D/g, "").slice(0, 6), business: null });

  return (
    <div className="space-y-3" data-testid={`${prefix}-customer-picker`}>
      <div className="grid grid-cols-2 gap-2">
        <TypeButton active={type === "existing"} label="Existing Customer" testid={`${prefix}-existing-customer-btn`}
          onClick={() => onChange({ type: "existing", number: "", business: null })} />
        <TypeButton active={type === "new"} label="New Customer" testid={`${prefix}-new-customer-btn`}
          onClick={() => onChange({ type: "new", number: "", business: null })} />
      </div>
      {type === "existing" && (
        <div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input data-testid={`${prefix}-customer-number-input`} inputMode="numeric" value={number}
              onChange={(e) => setNumber(e.target.value)} placeholder="Customer Number"
              className="w-full h-12 rounded-xl border border-slate-200 bg-white pl-10 pr-10 text-sm tracking-widest text-slate-900 outline-none focus:border-[#FFC400] focus:ring-2 focus:ring-[#FFC400]/30 transition" />
            {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
          </div>
          {value.business && (
            <p className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-700" data-testid={`${prefix}-customer-business-name`}>
              <Building2 className="h-4 w-4" /> {value.business.name}
            </p>
          )}
          {lookupError && !value.business && (
            <p className="mt-2 flex items-center gap-2 text-sm font-medium text-[#FF5A4D]" data-testid={`${prefix}-customer-lookup-error`}>
              <XCircle className="h-4 w-4" /> {lookupError}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerTypePicker;
